/**
 * AppVersionGate — Full-screen blocking overlay for two scenarios:
 *
 *  Case A: Android mobile browser (not Capacitor) → force native app install
 *  Case B: Native Capacitor app with outdated version → force update
 *
 * Fail-open: if version.json fetch fails, the gate does NOT block.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Smartphone, Download, RefreshCw } from 'lucide-react';
import { MODAL_COLORS } from '@/components/modal';
import { platform } from '@/utils/platform';
import { API_URL } from '@/network/http';

declare const __ANDROID_VERSION__: string;

const VERSION_JSON_URL = 'https://brainsway.report/version.json';

function apkUrl(version: string) {
  return `${API_URL}/uploads/file/releases/android/bw-report-${version}.apk`;
}

type GateReason = 'install' | 'update' | null;

/** Compare semver strings. Returns true if remote > local. */
function isNewerVersion(remote: string, local: string): boolean {
  const r = remote.split('.').map(Number);
  const l = local.split('.').map(Number);
  for (let i = 0; i < Math.max(r.length, l.length); i++) {
    const rv = r[i] ?? 0;
    const lv = l[i] ?? 0;
    if (rv > lv) return true;
    if (rv < lv) return false;
  }
  return false;
}

function isAndroidBrowser(): boolean {
  if (platform.isNative) return false;
  const ua = navigator.userAgent;
  // Must have Android in UA
  if (!/android/i.test(ua)) return false;
  // Exclude desktop OS (Chrome DevTools mobile emulation keeps the real platform)
  const plat = navigator.platform || '';
  if (/mac|win|linux/i.test(plat) && !/arm/i.test(plat)) return false;
  // Real Android browsers include "Mobile" in UA
  return /Mobile/i.test(ua);
}

export function AppVersionGate() {
  const { t } = useTranslation();
  const [reason, setReason] = useState<GateReason>(null);
  const [remoteVersion, setRemoteVersion] = useState<string>(__ANDROID_VERSION__);

  useEffect(() => {
    // Case A: Android mobile browser — always gate
    if (isAndroidBrowser()) {
      setReason('install');
      return;
    }

    // Case B: Native Android app — check for forced update
    if (platform.isNative && platform.isAndroid) {
      const localVersion = __ANDROID_VERSION__;
      if (!localVersion || localVersion === 'unknown') return;

      fetch(VERSION_JSON_URL, { cache: 'no-store' })
        .then((res) => {
          if (!res.ok) return;
          return res.json();
        })
        .then((data) => {
          if (data?.android && isNewerVersion(data.android, localVersion)) {
            setRemoteVersion(data.android);
            setReason('update');
          }
        })
        .catch(() => {
          // Fail-open: don't gate on network error
        });
    }
  }, []);

  if (!reason) return null;

  const isInstall = reason === 'install';
  const iconColor = isInstall ? '#16A34A' : MODAL_COLORS.header;
  const iconBg = isInstall ? '#F0FDF4' : '#EFF6FF';
  const buttonBg = isInstall ? '#16A34A' : MODAL_COLORS.header;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-6"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(8px)' }}
    >
      <div
        className="w-full max-w-sm rounded-3xl p-6 space-y-5 text-center"
        style={{ backgroundColor: '#ffffff' }}
      >
        {/* Icon */}
        <div
          className="mx-auto flex items-center justify-center rounded-full"
          style={{ width: 64, height: 64, backgroundColor: iconBg }}
        >
          {isInstall ? (
            <Smartphone size={32} style={{ color: iconColor }} />
          ) : (
            <RefreshCw size={32} style={{ color: iconColor }} />
          )}
        </div>

        {/* Title */}
        <h2 className="text-xl font-extrabold text-black">
          {isInstall ? t('appGate.installTitle') : t('appGate.updateTitle')}
        </h2>

        {/* Description */}
        <p className="text-sm" style={{ color: '#6B7280' }}>
          {isInstall ? t('appGate.installDescription') : t('appGate.updateDescription')}
        </p>

        {/* Download / Update button */}
        <a
          href={apkUrl(isInstall ? __ANDROID_VERSION__ : remoteVersion)}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full rounded-xl py-3.5 text-sm font-bold transition-all active:scale-[0.98] cursor-pointer flex items-center justify-center gap-2"
          style={{ backgroundColor: buttonBg, color: '#ffffff' }}
        >
          <Download size={16} />
          {isInstall ? t('appGate.installButton') : t('appGate.updateButton')}
        </a>

        {/* Install instructions (only for browser install case) */}
        {isInstall && (
          <div
            className="rounded-xl p-4 text-start space-y-1"
            style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0' }}
          >
            <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#166534' }}>
              {t('appGate.howToInstall')}
            </p>
            <ol className="text-xs space-y-1 list-decimal list-inside" style={{ color: '#15803D' }}>
              <li>{t('appGate.installStep1')}</li>
              <li>{t('appGate.installStep2')}</li>
              <li>{t('appGate.installStep3')}</li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}
